import { useCallback, useEffect, useState } from 'react'
import { Button, Col, DatePicker, Descriptions, Form, Input, InputNumber, Modal, Row, Select, Space, TableProps, Tooltip } from 'antd'
import { EyeOutlined, PlusOutlined, EditOutlined } from '@ant-design/icons'
import CustomTable from '../../components/table'
import defaultParams from '../../assets/constants/default_params'
import { toastError, toastSuccess } from '../../utils/toast'
import { useLoading } from '../../context/LoadingContext'
import { Link } from 'react-router-dom'
import { DETAIL, EXPORT, ORDER, SALES_ORDER } from '../../assets/constants/route_path'
import { filterOptionByLabel } from '../../utils/filter-utils'
import { orderStatusOptions } from '../../assets/constants/default_options'
import FormSearch from '../../components/form'
import { apiGetListCompanies, apiAddSalesOrderInventory, apiAddSalesOrderInfo, apiGetListOrderInventory, apiGetListSalesOrderInventory, apiUpdateSalesOrderInventory, apiGetSimInventoryByOrderIds } from '../../api'
import { ICompany } from '../../models/company.modal'
import { renderOrderStatus } from '../quanlytopup'
import { useSelector } from 'react-redux'
import { authSelector } from '../../redux/slices/authSlice'
import dayjs from 'dayjs'

const initOrderInfo = { cpCode: "", description: "" }

// One serial per line → trimmed non-empty strings.
const parseSerialsText = (text: string): string[] => text
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean)

// Sort ascending and fold consecutive same-prefix serials into ranges.
// e.g. [...001, ...002, ...003, ...010] → [{start: ...001, end: ...003}, {start: ...010, end: ...010}]
const groupConsecutiveSerials = (serials: string[]): { startSim: string; endSim: string }[] => {
    if (serials.length === 0) return []
    const sorted = [...serials].sort()
    const groups: { startSim: string; endSim: string }[] = []
    let start = sorted[0], end = sorted[0]
    for (let i = 1; i < sorted.length; i++) {
        const s = sorted[i]
        const samePrefix = s.slice(0, -5) === end.slice(0, -5)
        const isNext = Number(s.slice(-5)) === Number(end.slice(-5)) + 1
        if (samePrefix && isNext) {
            end = s
        } else {
            groups.push({ startSim: start, endSim: end })
            start = s
            end = s
        }
    }
    groups.push({ startSim: start, endSim: end })
    return groups
}

export default function SimExportSalesOrder() {
    const [companyOptions, setCompanyOptions] = useState<{ label: string, value: Number }[]>([])
    const [orderInventory, setOrderInventory] = useState<any>({})
    const [listSimByOrderIds, setListSimByOrderIds] = useState<any>([])
    const [currentPage, setCurrentPage] = useState<number>(defaultParams.PAGE)
    const [pageSize, setPageSize] = useState<number>(defaultParams.LIMIT)
    const [search, setSearch] = useState<any>({})
    const [modalUpload, setModalUpload] = useState<boolean>(false)
    const [orderInfo, setOrderInfo] = useState(initOrderInfo)
    const [serialsText, setSerialsText] = useState('')

    const [editModalOpen, setEditModalOpen] = useState(false)
    const [editingRow, setEditingRow] = useState<any>(null)
    const [editMode, setEditMode] = useState<'update' | 'addInfo'>('update')

    const { setIsLoading } = useLoading()
    const [formSearch] = Form.useForm()
    const [editForm] = Form.useForm()
    const { account } = useSelector(authSelector)

    const columns: TableProps<any>['columns'] = [
        {
            key: 'order_id',
            title: 'Mã đơn hàng',
            dataIndex: 'order_id',
            render: (order_id: any) => (
                <span style={{ whiteSpace: 'nowrap' }}>
                    {order_id}
                </span>
            ),
        },
        {
            key: 'owner_by',
            title: 'Nhân sự tạo',
            dataIndex: 'owner_by',
        },
        {
            key: 'customerName',
            title: 'Tên KH',
            dataIndex: ['salesOrderInfo', 'customerName'],
            render: (v?: string) => v || '—',
        },
        {
            key: 'customerPhone',
            title: 'SĐT KH',
            dataIndex: ['salesOrderInfo', 'customerPhone'],
            render: (v?: string) => v || '—',
        },
        {
            key: 'customerAddress',
            title: 'Địa chỉ',
            dataIndex: ['salesOrderInfo', 'customerAddress'],
            render: (v?: string) => v ? (
                <Tooltip title={v}>
                    <span style={{ display: 'inline-block', maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'middle' }}>
                        {v}
                    </span>
                </Tooltip>
            ) : '—',
        },
        {
            key: 'shipDate',
            title: 'Ngày gửi',
            dataIndex: ['salesOrderInfo', 'shipDate'],
            render: (v?: string) => v ? dayjs(v).format('DD/MM/YYYY') : '—',
        },
        {
            key: 'trackingCode',
            title: 'Mã VTP',
            dataIndex: ['salesOrderInfo', 'trackingCode'],
            render: (v?: string) => v ? (
                <Tooltip title={v}>
                    <span>{v}</span>
                </Tooltip>
            ) : '—',
        },
        {
            key: 'description',
            title: 'Nội dung',
            dataIndex: 'description',
        },
        {
            key: 'cpCode',
            title: 'Mã công ty',
            dataIndex: 'cpCode',
        },
        {
            key: 'totalSim',
            title: 'Tổng số sim',
            dataIndex: 'total_sim',
        },
        {
            key: 'total_success',
            title: 'Tổng thành công',
            dataIndex: 'total_success',
        },
        {
            key: 'total_fail',
            title: 'Tổng thất bại',
            dataIndex: 'total_fail',
        },
        {
            key: 'created_at',
            title: 'Ngày bán',
            dataIndex: 'created_at',
            render: (created_at: any) => dayjs(created_at).format('DD/MM/YYYY HH:mm:ss'),
        },
        {
            key: 'simInventory',
            title: 'Đã kích',
            dataIndex: 'simInventory',
            render: (simInventory: any) => simInventory?.reduce((acc: number, curr: any) => acc + curr.total_active, 0),
        },
        {
            key: 'status',
            title: 'Trạng thái',
            dataIndex: 'status',
            render: (status: any) => renderOrderStatus(status),
        },
        {
            key: 'action',
            title: 'Tác vụ',
            dataIndex: 'action',
            align: 'center',
            render: (_: any, row: any) => (
                <Space>
                    {account?.canGetListSimInventory && (
                        <Link to={EXPORT + ORDER + DETAIL + `?orderId=${row.order_id}`}>
                            <Button
                                icon={<EyeOutlined />}
                                style={{ background: '#289e45', color: 'white' }}
                            />
                        </Link>
                    )}
                    {row.hasSalesOrderInfo ? (
                        account?.canUpdateSalesOrderInventory && (
                            <Button
                                type="text"
                                icon={<EditOutlined />}
                                style={{ background: '#ff9800', color: 'white' }}
                                onClick={() => {
                                    setEditMode('update')
                                    setEditingRow(row)
                                    editForm.setFieldsValue({
                                        customerName: row.salesOrderInfo?.customerName,
                                        customerPhone: row.salesOrderInfo?.customerPhone,
                                        customerAddress: row.salesOrderInfo?.customerAddress,
                                        shipDate: row.salesOrderInfo?.shipDate ? dayjs(row.salesOrderInfo.shipDate) : dayjs(),
                                        trackingCode: row.salesOrderInfo?.trackingCode,
                                    })
                                    setEditModalOpen(true)
                                }}
                            />
                        )
                    ) : (
                        account?.canAddSalesOrderInfo && (
                            <Button
                                type="text"
                                icon={<PlusOutlined />}
                                style={{ background: '#1890ff', color: 'white' }}
                                onClick={() => {
                                    setEditMode('addInfo')
                                    setEditingRow(row)
                                    editForm.resetFields()
                                    editForm.setFieldsValue({ shipDate: dayjs() })
                                    setEditModalOpen(true)
                                }}
                            />
                        )
                    )}
                </Space>
            ),
        },
    ]

    const fetchData = useCallback(async () => {
        try {
            setIsLoading(true)
            // Retail scope guard — enterprise endpoint won't filter this for us.
            if (!search.cpCode) {
                setOrderInventory({ orderInventory: [], total: 0 })
                return
            }
            const { data: orderData } = await apiGetListOrderInventory({
                cpCode: search.cpCode,
                status: search.status,
                page: currentPage,
                size: pageSize,
                orderBy: 'created_at desc',
            })
            const orders: any[] = orderData?.orderInventory ?? []
            const orderIds = orders.map((o: any) => o.order_id).filter(Boolean)
            let infoByOrderId: Record<string, any> = {}
            if (orderIds.length > 0) {
                const { data: salesData } = await apiGetListSalesOrderInventory({ orderIds })
                for (const info of (salesData?.salesOrderInfo ?? [])) {
                    infoByOrderId[info.orderId] = info
                }
            }
            const merged = orders.map((o: any) => {
                const info = infoByOrderId[o.order_id]
                return { ...o, salesOrderInfo: info || null, hasSalesOrderInfo: !!info }
            })
            setOrderInventory({ orderInventory: merged, total: orderData?.total })
            mapData(merged)
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }, [setIsLoading, currentPage, pageSize, search])

    const mapData = async (data: any[]) => {
        try {
            if (!data || data.length === 0) return
            const orderIds = data.map((item: any) => item.order_id)
            const resp = await apiGetSimInventoryByOrderIds({ orderIds })
            const simInventoryData = resp.data.simInventory
            setOrderInventory((prev: any) => ({
                ...prev,
                orderInventory: data.map((item: any) => ({
                    ...item,
                    simInventory: simInventoryData.filter((sim: any) => sim.order_id === item.order_id),
                }))
            }))
            setListSimByOrderIds(simInventoryData)
        } catch (error) {
            console.log(error)
        }
    }

    useEffect(() => {
        fetchData()
    }, [fetchData])

    const fetchCompanies = useCallback(async () => {
        try {
            const { data } = await apiGetListCompanies({ limit: defaultParams.MAX_LIMIT })
            const options = data.companies?.filter((item: ICompany) => item.supportChannels?.includes('SALES_INVENTORY')).map((item: ICompany) => ({
                label: item.name,
                value: item.code
            }))
            setCompanyOptions(options)
            if (options?.length > 0) {
                const firstCpCode = options[0].value as any
                setOrderInfo(prev => prev.cpCode ? prev : { ...prev, cpCode: firstCpCode })
                // Scope list to first retail company on mount.
                setSearch((prev: any) => prev.cpCode ? prev : { ...prev, cpCode: firstCpCode })
                formSearch.setFieldsValue({ cpCode: firstCpCode })
            }
        } catch (error) {
            console.log(error)
        }
    }, [formSearch])

    useEffect(() => {
        fetchCompanies()
    }, [fetchCompanies])

    const handleSearch = ({ cpCode, status }: any) => {
        if (cpCode || status) {
            setSearch({ cpCode, status })
            setCurrentPage(defaultParams.PAGE)
            setPageSize(defaultParams.LIMIT)
        }
    }

    const handleClearSearch = () => {
        formSearch.resetFields()
        // Preserve retail-scope: always keep the current company selection.
        const firstCpCode = companyOptions[0]?.value as any
        if (firstCpCode) {
            formSearch.setFieldsValue({ cpCode: firstCpCode })
            setSearch({ cpCode: firstCpCode })
        } else {
            setSearch({})
        }
        setCurrentPage(defaultParams.PAGE)
        setPageSize(defaultParams.LIMIT)
    }

    const validateSerials = (serials: string[]): boolean => {
        if (serials.length === 0) {
            toastError('Vui lòng nhập ít nhất 1 serial')
            return false
        }
        const isValid = serials.every((s) => s.length >= 19 && /^\d+$/.test(s))
        if (!isValid) {
            toastError('Serial Sim không hợp lệ (mỗi dòng phải là chuỗi số ≥19 ký tự)')
            return false
        }
        const seen = new Set<string>()
        for (const s of serials) {
            if (seen.has(s)) {
                toastError(`Serial trùng nhau: ${s}`)
                return false
            }
            seen.add(s)
        }
        return true
    }

    const handleAddOrderInventory = async (formValues: any) => {
        try {
            setIsLoading(true)
            if (!orderInfo.cpCode) {
                toastError('Vui lòng chọn công ty')
                return
            }
            if (!orderInfo.description.trim()) {
                toastError('Vui lòng nhập mô tả')
                return
            }
            const rawSerials = parseSerialsText(serialsText)
            if (!validateSerials(rawSerials)) return
            const simInventory = groupConsecutiveSerials(rawSerials)

            await apiAddSalesOrderInventory({
                cpCode: orderInfo.cpCode,
                description: orderInfo.description,
                simInventory,
                customerName: formValues.customerName.trim(),
                customerPhone: formValues.customerPhone.trim(),
                customerAddress: formValues.customerAddress.trim(),
                shipDate: formValues.shipDate.toISOString(),
                trackingCode: formValues.trackingCode.trim(),
            })

            fetchData()
            handleCloseModalAddOrderInventory()
            toastSuccess('Tạo đơn thành công! Vui lòng kiểm tra kết quả sau ít phút')
        } catch (error: any) {
            console.log(error);
            toastError(error?.response?.data?.message || 'Có lỗi xảy ra!')
        } finally {
            setIsLoading(false)
        }
    }

    const handleCloseModalAddOrderInventory = () => {
        setModalUpload(false)
        setOrderInfo({ ...initOrderInfo, cpCode: (companyOptions[0]?.value as any) || "" })
        setSerialsText('')
    }

    const handleEditSubmit = async () => {
        try {
            const values = await editForm.validateFields()
            const payload = {
                orderId: editingRow.order_id,
                customerName: values.customerName.trim(),
                customerPhone: values.customerPhone.trim(),
                customerAddress: values.customerAddress.trim(),
                shipDate: values.shipDate.toISOString(),
                trackingCode: values.trackingCode.trim(),
            }
            setIsLoading(true)
            if (editMode === 'update') {
                await apiUpdateSalesOrderInventory(payload)
            } else {
                await apiAddSalesOrderInfo(payload)
            }
            toastSuccess(editMode === 'update' ? 'Cập nhật thành công' : 'Thêm thông tin thành công')
            setEditModalOpen(false)
            fetchData()
        } catch (err: any) {
            toastError(err?.message || 'Lỗi máy chủ')
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div>
            <FormSearch form={formSearch} handleSearch={handleSearch} handleClearSearch={handleClearSearch}>
                <Form.Item name='cpCode' label='Công ty'>
                    <Select
                        style={{ minWidth: 170 }}
                        showSearch
                        placeholder="Chọn công ty"
                        options={companyOptions}
                        filterOption={filterOptionByLabel}
                    />
                </Form.Item>
                <Form.Item name='status' label='Trạng thái'>
                    <Select
                        style={{ width: 170 }}
                        showSearch
                        placeholder="Chọn trạng thái"
                        options={orderStatusOptions}
                        filterOption={filterOptionByLabel}
                    />
                </Form.Item>
            </FormSearch>
            {account?.canAddSalesOrderInventory && (
                <div style={{ textAlign: 'end', marginBottom: 20 }}>
                    <Button onClick={() => setModalUpload(true)} type='primary'>Thêm đơn hàng</Button>
                </div>
            )}
            <Descriptions
                style={{ maxWidth: 600 }}
                items={[
                    {
                        key: 'totalOrder',
                        label: 'Tổng đơn hiện thị',
                        children: orderInventory.orderInventory?.length || 0,
                    },
                    {
                        key: 'totalSuccess',
                        label: 'Tổng thành công',
                        children: orderInventory.orderInventory?.reduce((acc: number, curr: any) => acc + curr.total_success, 0) || 0,
                    },
                    {
                        key: 'totalActive',
                        label: 'Tổng đã kích',
                        children: listSimByOrderIds?.reduce((acc: number, curr: any) => acc + curr.total_active, 0) || 0,
                    },
                ]}
            />
            <CustomTable
                namePage='đơn hàng bán lẻ'
                columns={columns}
                dataSource={orderInventory.orderInventory}
                pageSize={pageSize}
                setPageSize={setPageSize}
                total={orderInventory.total}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
            />

            {/* Add Order Modal — full create with shipping fields */}
            <Modal title='Thêm đơn hàng bán lẻ' open={modalUpload}
                onCancel={handleCloseModalAddOrderInventory}
                footer={null}
                width={760}
            >
                <Form
                    layout="vertical"
                    autoComplete="off"
                    onFinish={handleAddOrderInventory}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label='Công ty' required>
                                <Select
                                    value={orderInfo.cpCode}
                                    onChange={value => setOrderInfo({ ...orderInfo, cpCode: value })}
                                    showSearch
                                    placeholder="Chọn công ty"
                                    options={companyOptions}
                                    filterOption={filterOptionByLabel}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item
                        label='Danh sách Serial Sim'
                        required
                        help='Mỗi dòng 1 serial (≥19 chữ số). UI tự gom các serial liên tiếp thành 1 dải.'
                    >
                        <Input.TextArea
                            rows={5}
                            value={serialsText}
                            onChange={(e) => setSerialsText(e.target.value)}
                            placeholder={'8984048000900700001\n8984048000900700002\n8984048000900700003'}
                        />
                        {serialsText.trim() && (() => {
                            const rawSerials = parseSerialsText(serialsText)
                            const groups = groupConsecutiveSerials(rawSerials)
                            return (
                                <div style={{ marginTop: 4, color: '#666', fontSize: 12 }}>
                                    Tổng: <b>{rawSerials.length}</b> sim · gom thành <b>{groups.length}</b> dải
                                </div>
                            )
                        })()}
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Tên khách hàng"
                                name="customerName"
                                rules={[{ required: true, message: 'Vui lòng nhập tên khách hàng' }]}
                            >
                                <Input placeholder="Nguyễn Văn A" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="SĐT khách hàng"
                                name="customerPhone"
                                rules={[
                                    { required: true, message: 'Vui lòng nhập SĐT' },
                                    { pattern: /^(0|\+84)\d{9,10}$/, message: 'SĐT không hợp lệ' },
                                ]}
                            >
                                <Input placeholder="0901234567" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item
                        label="Địa chỉ"
                        name="customerAddress"
                        rules={[{ required: true, message: 'Vui lòng nhập địa chỉ' }]}
                    >
                        <Input.TextArea rows={2} placeholder="123 Đường ABC, Quận X, TP Y" />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Ngày gửi"
                                name="shipDate"
                                rules={[{ required: true, message: 'Vui lòng chọn ngày gửi' }]}
                                initialValue={dayjs()}
                            >
                                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Mã vận đơn VTP"
                                name="trackingCode"
                                rules={[{ required: true, message: 'Vui lòng nhập mã vận đơn Viettel Post' }]}
                            >
                                <Input placeholder="e.g. VTP1234567890" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item
                        label='Mô tả'
                        required
                        validateStatus={orderInfo.description.trim() === '' ? undefined : 'success'}
                    >
                        <Input.TextArea
                            rows={2}
                            value={orderInfo.description}
                            onChange={(e) => setOrderInfo({ ...orderInfo, description: e.target.value })}
                            placeholder='Nội dung đơn'
                        />
                    </Form.Item>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        <Button htmlType='submit' style={{ background: '#008965', color: 'white' }}>
                            Xác nhận
                        </Button>
                        <Button onClick={handleCloseModalAddOrderInventory} style={{ background: 'gray', color: 'white' }}>
                            Đóng
                        </Button>
                    </div>
                </Form>
            </Modal>

            {/* Edit / AddInfo Modal — shipping-only update */}
            <Modal
                title={editMode === 'update' ? 'Sửa thông tin bán lẻ' : 'Thêm thông tin bán lẻ'}
                open={editModalOpen}
                onCancel={() => {
                    setEditModalOpen(false)
                    editForm.resetFields()
                }}
                footer={[
                    <Button
                        key="submit"
                        onClick={handleEditSubmit}
                        style={{ background: '#ff9800', color: 'white' }}
                    >
                        {editMode === 'update' ? 'Cập nhật' : 'Thêm'}
                    </Button>,
                    <Button
                        key="cancel"
                        onClick={() => {
                            setEditModalOpen(false)
                            editForm.resetFields()
                        }}
                        style={{ background: 'gray', color: 'white' }}
                    >
                        Đóng
                    </Button>,
                ]}
                width={520}
            >
                {/* Display-only order info */}
                <div style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', gap: 20, marginBottom: 8 }}>
                        <div style={{ flex: 1 }}>
                            <b>Mã đơn hàng:</b> {editingRow?.order_id}
                        </div>
                        <div style={{ flex: 1 }}>
                            <b>Công ty:</b> {editingRow?.cpCode}
                        </div>
                    </div>
                </div>
                <Form form={editForm} layout="vertical" autoComplete="off">
                    <Form.Item
                        label="Tên khách hàng"
                        name="customerName"
                        rules={[{ required: true, message: 'Vui lòng nhập tên khách hàng' }]}
                    >
                        <Input placeholder="Nguyễn Văn A" />
                    </Form.Item>
                    <Form.Item
                        label="SĐT khách hàng"
                        name="customerPhone"
                        rules={[
                            { required: true, message: 'Vui lòng nhập SĐT' },
                            { pattern: /^(0|\+84)\d{9,10}$/, message: 'SĐT không hợp lệ' },
                        ]}
                    >
                        <Input placeholder="0901234567" />
                    </Form.Item>
                    <Form.Item
                        label="Địa chỉ"
                        name="customerAddress"
                        rules={[{ required: true, message: 'Vui lòng nhập địa chỉ' }]}
                    >
                        <Input.TextArea rows={2} placeholder="123 Đường ABC, Quận X, TP Y" />
                    </Form.Item>
                    <Form.Item
                        label="Ngày gửi"
                        name="shipDate"
                        rules={[{ required: true, message: 'Vui lòng chọn ngày gửi' }]}
                    >
                        <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                        label="Mã vận đơn VTP"
                        name="trackingCode"
                        rules={[{ required: true, message: 'Vui lòng nhập mã vận đơn Viettel Post' }]}
                    >
                        <Input placeholder="e.g. VTP1234567890" />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    )
}
